import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaStop, FaPlus, FaMinus, FaCheckCircle, FaHistory, FaStopCircle, FaPlayCircle, FaTrophy, FaChartBar, FaClipboardList, FaClock } from 'react-icons/fa';
import { liveEventApi, scorecardUtils } from '../../api/scorecard';
import GameTimer from './GameTimer';
import styles from './LiveScorecardModal.module.css';

const LiveScorecardModal = ({ event, onClose, onEventUpdated }) => {
  const [activeTab, setActiveTab] = useState('score'); // 'score', 'statistics', 'attendance', 'log'
  const [liveSession, setLiveSession] = useState(event?.liveSession);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Timer state
  const [currentTimer, setCurrentTimer] = useState(null);
  // Key to trigger GameTimer refresh after period changes
  const [timerRefreshKey, setTimerRefreshKey] = useState(0);

  // Score state
  const [homeScore, setHomeScore] = useState(event?.liveSession?.scorecard?.score?.home || 0);
  const [awayScore, setAwayScore] = useState(event?.liveSession?.scorecard?.score?.away || 0);

  // Statistics state
  const [homeStats, setHomeStats] = useState(event?.liveSession?.scorecard?.statistics?.home || {});
  const [awayStats, setAwayStats] = useState(event?.liveSession?.scorecard?.statistics?.away || {});

  // Event log state
  const [eventLogType, setEventLogType] = useState('');
  const [eventLogTeam, setEventLogTeam] = useState('home');
  const [eventLogDescription, setEventLogDescription] = useState('');
  const [eventLogMinute, setEventLogMinute] = useState('');
  const [minuteAutoFilled, setMinuteAutoFilled] = useState(false);

  // Attendance state
  const [attendanceData, setAttendanceData] = useState({});
  const [attendanceSearchTerm, setAttendanceSearchTerm] = useState('');
  const [attendanceFilter, setAttendanceFilter] = useState('all'); // 'all', 'unmarked', 'present', 'absent', 'late', 'excused'

  const schema = liveSession?.scorecard?.templateSnapshot?.schema;
  const layout = liveSession?.scorecard?.templateSnapshot?.layout;
  const isEventCompleted = event?.status === 'Completed';

  // Compute game state for timer controls
  const gameState = useMemo(() => {
    const periods = liveSession?.scorecard?.score?.periods || [];
    const overtimePeriods = liveSession?.scorecard?.score?.overtimePeriods || [];
    
    const currentPeriod = periods.find(p => p.isActive);
    const currentOvertimePeriod = overtimePeriods.find(p => p.isActive);
    const completedCount = periods.filter(p => p.finishedAt).length;
    
    const totalPeriods = schema?.periods?.count || 2;
    const allRegularPeriodsComplete = completedCount >= totalPeriods;
    
    const overtimeEnabled = schema?.overtime?.enabled;
    const scoreIsTied = homeScore === awayScore;
    const maxOvertimeCount = schema?.overtime?.maxCount || 2;
    const overtimeCount = overtimePeriods.length;
    
    const hasActivePeriod = !!(currentPeriod || currentOvertimePeriod);
    const gameDecided = !scoreIsTied;
    
    // All periods complete when: regular periods done AND (not tied OR no overtime available OR max overtime reached)
    const allPeriodsComplete = allRegularPeriodsComplete && 
      !currentPeriod && 
      !currentOvertimePeriod && (
        !scoreIsTied || 
        !overtimeEnabled || 
        overtimeCount >= maxOvertimeCount
      );
    
    return {
      hasActivePeriod,
      allPeriodsComplete,
      gameDecided,
      periodsEnabled: schema?.periods?.enabled || false,
    };
  }, [liveSession, homeScore, awayScore, schema]);

  // Handle timer updates - MEMOIZED to prevent infinite re-renders!
  const handleTimerUpdate = useCallback((timerData) => {
    setCurrentTimer(timerData);
  }, []); // Empty deps = stable function reference

  // Auto-update minute field when timer changes (if it was auto-filled)
  useEffect(() => {
    if (activeTab === 'log' && currentTimer?.displayMinutes !== undefined) {
      // Only auto-update if field is empty OR still shows the auto-filled value
      if (!eventLogMinute || minuteAutoFilled) {
        setEventLogMinute(currentTimer.displayMinutes.toString());
        setMinuteAutoFilled(true);
      }
    }
  }, [currentTimer?.displayMinutes, activeTab]);

  useEffect(() => {
    // Auto-dismiss success messages
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Sync liveSession state when event prop changes
  useEffect(() => {
    if (event?.liveSession) {
      console.log('🔄 Event prop changed, syncing liveSession state');
      setLiveSession(event.liveSession);
      // Also sync scores
      setHomeScore(event.liveSession.scorecard.score.home);
      setAwayScore(event.liveSession.scorecard.score.away);
    }
  }, [event?.liveSession?.scorecard?.lastUpdated]); // Trigger when scorecard is updated

  // Initialize attendance data from expected attendees
  useEffect(() => {
    if (liveSession?.attendance?.enabled) {
      const initialData = {};
      
      // Pre-populate from existing attendees
      liveSession.attendance.attendees?.forEach(attendee => {
        initialData[attendee.user._id || attendee.user] = {
          status: attendee.status,
          notes: attendee.notes || '',
          arrivedAt: attendee.arrivedAt
        };
      });

      // Add expected attendees who haven't been marked yet
      liveSession.attendance.expectedAttendees?.forEach(expected => {
        // Handle both old format (user as object) and new denormalized format (user as string)
        const userId = (typeof expected.user === 'object' ? expected.user._id : expected.user) || expected.user;
        if (!initialData[userId]) {
          // Smart default based on RSVP status
          let defaultStatus = 'unmarked';
          if (expected.rsvpStatus === 'Going') {
            defaultStatus = 'present';
          } else if (expected.rsvpStatus === 'Not Going') {
            defaultStatus = 'absent';
          }
          
          initialData[userId] = {
            status: defaultStatus,
            notes: '',
            arrivedAt: null
          };
        }
      });

      setAttendanceData(initialData);
    }
  }, [liveSession?.attendance]);

  const getStatIncrement = useCallback((stat) => {
    if (stat.increment !== undefined) {
      return stat.increment;
    }
    switch (stat.type) {
      case 'percentage':
        return 5;
      case 'time':
        return 1;
      case 'counter':
      case 'number':
      default:
        return 1;
    }
  }, []);

  const getStatBounds = useCallback((stat) => {
    const min = stat.min ?? 0;
    const max = stat.max ?? (stat.type === 'percentage' ? 100 : 999);
    return { min, max };
  }, []);

  const adjustStat = useCallback(async (team, statKey, delta, stat) => {
    const { min, max } = getStatBounds(stat);
    
    const currentStats = team === 'home' ? homeStats : awayStats;
    const currentValue = currentStats[statKey] ?? stat.defaultValue ?? 0;
    const newValue = Math.max(min, Math.min(max, currentValue + delta));
    
    if (newValue === currentValue) return;
    
    const newStats = { ...currentStats, [statKey]: newValue };
    
    if (team === 'home') {
      setHomeStats(newStats);
    } else {
      setAwayStats(newStats);
    }

    try {
      await liveEventApi.updateStatistics(event._id, team, newStats);
      onEventUpdated?.();
    } catch (err) {
      console.error('Error auto-saving stat:', err);
      if (team === 'home') {
        setHomeStats(currentStats);
      } else {
        setAwayStats(currentStats);
      }
      setError(err.message || 'Could not save stat. Please try again.');
    }
  }, [event._id, getStatBounds, onEventUpdated, homeStats, awayStats]);

  const setStatDirectly = useCallback(async (team, statKey, newValue, stat) => {
    const { min, max } = getStatBounds(stat);
    
    const clampedValue = Math.max(min, Math.min(max, newValue));
    
    const currentStats = team === 'home' ? homeStats : awayStats;
    const newStats = { ...currentStats, [statKey]: clampedValue };
    
    if (team === 'home') {
      setHomeStats(newStats);
    } else {
      setAwayStats(newStats);
    }

    try {
      await liveEventApi.updateStatistics(event._id, team, newStats);
      onEventUpdated?.();
    } catch (err) {
      console.error('Error saving stat:', err);
      if (team === 'home') {
        setHomeStats(currentStats);
      } else {
        setAwayStats(currentStats);
      }
      setError(err.message || 'Could not save stat. Please try again.');
    }
  }, [event._id, getStatBounds, onEventUpdated, homeStats, awayStats]);

  // Validate event structure AFTER all hooks
  if (!event?.liveSession?.scorecard?.templateSnapshot?.schema) {
    return createPortal(
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2>Error</h2>
            <button onClick={onClose} className={styles.closeButton}>
              <FaTimes />
            </button>
          </div>
          <div className={styles.errorBanner}>
            Invalid event data structure. Please try again.
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const updateScore = async () => {
    try {
      setUpdating(true);
      setError(null);

      // Validate score
      const errors = scorecardUtils.validateScoreUpdate(
        { home: homeScore, away: awayScore },
        schema
      );
      if (errors.length > 0) {
        throw new Error(errors.join(', '));
      }

      const response = await liveEventApi.updateScore(event._id, {
        home: homeScore,
        away: awayScore
      });

      console.log('Score update response (updateScore):', response);

      // Update the entire liveSession from the response
      if (response.event?.liveSession) {
        setLiveSession(response.event.liveSession);
      } else if (response.scorecard) {
        setLiveSession(prev => ({
          ...prev,
          scorecard: response.scorecard
        }));
      } else {
        // Fallback to old structure
        setLiveSession(prev => ({
          ...prev,
          scorecard: {
            ...prev.scorecard,
            score: response.score,
            lastUpdated: response.lastUpdated
          }
        }));
      }

      setSuccessMessage('Score updated successfully!');
      onEventUpdated?.();
    } catch (err) {
      console.error('Error updating score:', err);
      setError(err.message || 'Failed to update score');
    } finally {
      setUpdating(false);
    }
  };

  const adjustScore = async (team, amount) => {
    // Don't allow if already updating
    if (updating) {
      console.log('Already updating, ignoring click');
      return;
    }

    try {
      setUpdating(true);
      setError(null);
      setSuccessMessage(null);
      
      const newScore = team === 'home' 
        ? Math.max(0, homeScore + amount)
        : Math.max(0, awayScore + amount);

      // Update UI immediately for responsiveness
      if (team === 'home') {
        setHomeScore(newScore);
      } else {
        setAwayScore(newScore);
      }

      // Then update backend
      if (team === 'home') {
        await updateScoreImmediately(newScore, awayScore);
      } else {
        await updateScoreImmediately(homeScore, newScore);
      }

      setSuccessMessage('Score updated!');
    } catch (err) {
      console.error('Error adjusting score:', err);
      // Revert to backend score on error
      const backendScore = liveSession?.scorecard?.score;
      setHomeScore(backendScore?.home || 0);
      setAwayScore(backendScore?.away || 0);
      setError(err.message || 'Failed to update score');
    } finally {
      setUpdating(false);
    }
  };

  const updateScoreImmediately = async (home, away) => {
    try {
      const response = await liveEventApi.updateScore(event._id, {
        home,
        away
      });

      console.log('🎯 Score update response:', response);
      console.log('🎯 Response structure:', {
        hasEvent: !!response.event,
        hasLiveSession: !!response.event?.liveSession,
        hasScorecard: !!response.scorecard,
        hasScore: !!response.score,
        hasPeriods: !!response.scorecard?.score?.periods || !!response.score?.periods,
        periodCount: response.scorecard?.score?.periods?.length || response.score?.periods?.length || 0
      });

      // NEW API: Backend now returns full scorecard object
      if (response.scorecard) {
        console.log('✅ Updating from response.scorecard (NEW API)');
        console.log('Period scores:', response.scorecard.score.periods);
        setLiveSession(prev => ({
          ...prev,
          scorecard: response.scorecard  // Replace entire scorecard
        }));
        
        // Also update local score state
        setHomeScore(response.scorecard.score.home);
        setAwayScore(response.scorecard.score.away);
      } 
      // LEGACY: Support old response format (will be removed in future)
      else if (response.event?.liveSession) {
        console.log('✅ Updating from response.event.liveSession (LEGACY API)');
        console.log('Period scores:', response.event.liveSession.scorecard.score.periods);
        setLiveSession(response.event.liveSession);
        
        const score = response.event.liveSession.scorecard.score;
        setHomeScore(score.home);
        setAwayScore(score.away);
      } 
      // FALLBACK: Old API (shouldn't happen after backend fix)
      else if (response.score) {
        console.warn('⚠️ OLD API RESPONSE: Updating from response.score (backend not updated?)');
        console.log('Period scores in response.score:', response.score.periods);
        setLiveSession(prev => ({
          ...prev,
          scorecard: {
            ...prev.scorecard,
            score: response.score
          }
        }));
        
        setHomeScore(response.score.home);
        setAwayScore(response.score.away);
      } else {
        console.error('❌ UNKNOWN RESPONSE STRUCTURE:', response);
        throw new Error('Invalid API response structure');
      }

      // Notify parent to refresh
      onEventUpdated?.();
    } catch (err) {
      console.error('Error updating score:', err);
      // Show detailed backend error message
      const errorMessage = err.message || 'Failed to update score';
      setError(`Score update failed: ${errorMessage}. Check browser console for details.`);
      throw err; // Re-throw so adjustScore can handle it
    }
  };

  const addEventLog = async () => {
    try {
      setUpdating(true);
      setError(null);

      if (!eventLogType || !eventLogDescription) {
        throw new Error('Please fill in all required fields');
      }

      const eventType = schema?.eventTypes?.find(et => et.type === eventLogType);
      if (!eventType) {
        throw new Error('Invalid event type');
      }

      const eventData = {
        type: eventLogType,
        team: eventLogTeam,
        description: eventLogDescription,
        minute: parseInt(eventLogMinute) || 0,
        points: eventType.affectsScore ? eventType.pointValues[0] || 0 : 0
      };

      const response = await liveEventApi.addEventLog(event._id, eventData);

      console.log('📝 Event log response:', response);
      console.log('📝 Response structure:', {
        hasScorecard: !!response.scorecard,
        hasEventLog: !!response.eventLog,
        hasUpdatedScore: !!response.updatedScore,
        hasPeriods: !!response.scorecard?.score?.periods,
        minuteAutoFilled: response.minuteAutoFilled
      });

      // NEW API: Backend now returns full scorecard object
      if (response.scorecard) {
        console.log('✅ Updating from response.scorecard (NEW API)');
        console.log('Period scores:', response.scorecard.score.periods);
        console.log('Events count:', response.scorecard.events?.length);
        
        setLiveSession(prev => ({
          ...prev,
          scorecard: response.scorecard  // Replace entire scorecard (includes events!)
        }));
        
        // Update local score state
        setHomeScore(response.scorecard.score.home);
        setAwayScore(response.scorecard.score.away);
      } 
      // LEGACY: Support old response format
      else if (response.event?.liveSession) {
        console.log('✅ Updating from response.event.liveSession (LEGACY API)');
        setLiveSession(response.event.liveSession);
        
        const score = response.event.liveSession.scorecard.score;
        setHomeScore(score.home);
        setAwayScore(score.away);
      } 
      // FALLBACK: Old API (shouldn't happen after backend fix)
      else {
        console.warn('⚠️ OLD API RESPONSE: Using fallback update (backend not updated?)');
        setLiveSession(prev => ({
          ...prev,
          scorecard: {
            ...prev.scorecard,
            events: [...prev.scorecard.events, response.eventLog],
            score: response.updatedScore || prev.scorecard.score
          }
        }));
        
        if (response.updatedScore) {
          setHomeScore(response.updatedScore.home);
          setAwayScore(response.updatedScore.away);
        }
      }

      // Clear form
      setEventLogType('');
      setEventLogDescription('');
      setEventLogMinute('');

      setSuccessMessage('Event logged successfully!');
      onEventUpdated?.();
    } catch (err) {
      console.error('Error adding event log:', err);
      setError(err.message || 'Failed to add event log');
    } finally {
      setUpdating(false);
    }
  };

  const finishEvent = async () => {
    if (!window.confirm('Are you sure you want to finish this event? This cannot be undone.')) {
      return;
    }

    try {
      setUpdating(true);
      setError(null);

      await liveEventApi.finishEvent(event._id, 'Event completed');

      setSuccessMessage('Event finished successfully!');
      
      // Close modal and refresh
      setTimeout(() => {
        onEventUpdated?.();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error finishing event:', err);
      setError(err.message || 'Failed to finish event');
      setUpdating(false);
    }
  };

  const handleEndPeriod = async () => {
    if (!window.confirm('Are you sure you want to end the current period?')) {
      return;
    }

    try {
      setUpdating(true);
      setError(null);
      
      // CRITICAL: Pause timer before ending period to preserve time
      try {
        console.log('⏱️ Pausing timer for period end...');
        await liveEventApi.pauseTimer(event._id, 'period_ended');
        console.log('✅ Timer paused');
      } catch (timerError) {
        // Timer might already be paused or not running - that's okay
        console.log('⏱️ Timer pause skipped:', timerError.message);
      }
      
      const response = await liveEventApi.endPeriod(event._id);
      
      // Update local state with the new liveSession data
      if (response.event?.liveSession) {
        setLiveSession(response.event.liveSession);
        
        // Also update local score state to match
        const newScore = response.event.liveSession.scorecard?.score;
        if (newScore) {
          setHomeScore(newScore.home);
          setAwayScore(newScore.away);
        }
      }
      
      // Fetch fresh timer state to ensure it's synced
      try {
        const timerResponse = await liveEventApi.getTimer(event._id);
        if (timerResponse?.timer) {
          setCurrentTimer(timerResponse.timer);
        }
      } catch (e) {
        // Ignore timer fetch errors
      }
      
      // Trigger GameTimer to re-sync (to show paused state)
      setTimerRefreshKey(prev => prev + 1);
      
      const periodName = response.periodEnded?.name || 'Period';
      setSuccessMessage(`${periodName} ended! Timer paused.`);
      // Trigger parent to refetch fresh event data
      onEventUpdated?.();
    } catch (err) {
      const errorMsg = err.message?.toLowerCase() || '';
      
      // Provide user-friendly messages for common errors
      if (errorMsg.includes('no active period') || errorMsg.includes('not active')) {
        setError('There is no period currently in progress to end.');
      } else {
        setError(err.message || 'Failed to end period');
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleStartPeriod = async () => {
    try {
      setUpdating(true);
      setError(null);
      
      const response = await liveEventApi.startPeriod(event._id);
      
      // Update local state with the new scorecard data
      if (response.event?.liveSession) {
        setLiveSession(response.event.liveSession);
      }
      
      // Timer handling: Check if already running, then reset/start as needed
      try {
        const currentTimerState = await liveEventApi.getTimer(event._id);
        
        if (currentTimerState?.timer?.isRunning) {
          // Timer already running - just sync the state
          console.log('⏱️ Timer already running, syncing state...');
          setCurrentTimer(currentTimerState.timer);
        } else {
          // Timer not running - reset to 0 and start
          console.log('⏱️ Starting timer for new period...');
          // First reset to 0 without auto-start
          await liveEventApi.resetTimer(event._id, 'period_start', false);
          // Then start the timer
          await liveEventApi.startTimer(event._id);
          // Fetch fresh timer state
          const timerResponse = await liveEventApi.getTimer(event._id);
          if (timerResponse?.timer) {
            setCurrentTimer(timerResponse.timer);
          }
        }
        // Trigger GameTimer to re-sync
        setTimerRefreshKey(prev => prev + 1);
        console.log('✅ Timer ready for new period');
      } catch (timerError) {
        console.warn('⚠️ Timer setup warning:', timerError.message);
        // Try to just fetch current state as fallback
        try {
          const timerState = await liveEventApi.getTimer(event._id);
          if (timerState?.timer) {
            setCurrentTimer(timerState.timer);
          }
          setTimerRefreshKey(prev => prev + 1);
        } catch (e) {
          // Ignore - timer might not be enabled
        }
      }
      
      const periodName = response.periodStarted?.name || response.period?.name || 'Period';
      setSuccessMessage(`${periodName} started! Timer running.`);
      onEventUpdated?.();
    } catch (err) {
      const errorMsg = err.message?.toLowerCase() || '';
      
      // Provide user-friendly messages for common errors
      if (errorMsg.includes('period number must be between') || errorMsg.includes('invalid period number')) {
        setError('All regular periods have been played. Use "Add Extra Time" if the score is tied, or "Finish Game" to complete the event.');
      } else if (errorMsg.includes('already active') || errorMsg.includes('already started')) {
        setError('A period is already in progress. End the current period before starting a new one.');
      } else {
        setError(err.message || 'Failed to start period');
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleAddOvertime = async () => {
    try {
      setUpdating(true);
      setError(null);
      
      const response = await liveEventApi.addOvertimePeriod(event._id);
      
      // Update local state with the new event data
      if (response.event?.liveSession) {
        setLiveSession(response.event.liveSession);
      }
      
      // Timer handling: Check if already running, then reset/start as needed
      try {
        const currentTimerState = await liveEventApi.getTimer(event._id);
        
        if (currentTimerState?.timer?.isRunning) {
          // Timer already running - just sync the state
          console.log('⏱️ Timer already running for overtime, syncing state...');
          setCurrentTimer(currentTimerState.timer);
        } else {
          // Timer not running - reset to 0 and start
          console.log('⏱️ Starting timer for overtime...');
          // First reset to 0 without auto-start
          await liveEventApi.resetTimer(event._id, 'overtime_start', false);
          // Then start the timer
          await liveEventApi.startTimer(event._id);
          // Fetch fresh timer state
          const timerResponse = await liveEventApi.getTimer(event._id);
          if (timerResponse?.timer) {
            setCurrentTimer(timerResponse.timer);
          }
        }
        // Trigger GameTimer to re-sync
        setTimerRefreshKey(prev => prev + 1);
        console.log('✅ Timer ready for overtime');
      } catch (timerError) {
        console.warn('⚠️ Timer setup warning:', timerError.message);
        // Try to just fetch current state as fallback
        try {
          const timerState = await liveEventApi.getTimer(event._id);
          if (timerState?.timer) {
            setCurrentTimer(timerState.timer);
          }
          setTimerRefreshKey(prev => prev + 1);
        } catch (e) {
          // Ignore - timer might not be enabled
        }
      }
      
      const periodName = response.overtimePeriod?.name || 'Extra Time';
      setSuccessMessage(`${periodName} started! Timer running.`);
      onEventUpdated?.();
    } catch (err) {
      const errorMsg = err.message?.toLowerCase() || '';
      
      // Provide user-friendly messages for common errors
      if (errorMsg.includes('not tied') || errorMsg.includes('score must be tied')) {
        setError('Extra time can only be added when the score is tied. A winner has been decided.');
      } else if (errorMsg.includes('max overtime') || errorMsg.includes('maximum overtime')) {
        setError('Maximum extra time periods reached. Please finish the game.');
      } else if (errorMsg.includes('not enabled') || errorMsg.includes('overtime not configured')) {
        setError('Extra time is not configured for this event type.');
      } else if (errorMsg.includes('periods not complete') || errorMsg.includes('regular periods')) {
        setError('All regular periods must be completed before adding extra time.');
      } else {
        setError(err.message || 'Failed to add overtime');
      }
    } finally {
      setUpdating(false);
    }
  };

  // ==================== ATTENDANCE FUNCTIONS ====================

  const handleAttendanceStatusChange = (userId, newStatus) => {
    setAttendanceData(prev => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        status: newStatus,
        arrivedAt: newStatus === 'present' ? new Date().toISOString() : prev[userId]?.arrivedAt
      }
    }));
  };

  const handleMarkAllAs = (status) => {
    const updatedData = {};
    Object.keys(attendanceData).forEach(userId => {
      updatedData[userId] = {
        ...attendanceData[userId],
        status: status,
        arrivedAt: status === 'present' ? new Date().toISOString() : attendanceData[userId]?.arrivedAt
      };
    });
    setAttendanceData(updatedData);
  };

  const handleMarkRSVPGroup = (rsvpStatus, attendanceStatus) => {
    const updatedData = { ...attendanceData };
    liveSession.attendance.expectedAttendees?.forEach(expected => {
      if (expected.rsvpStatus === rsvpStatus) {
        // Handle both old format (user as object) and new denormalized format (user as string)
        const userId = (typeof expected.user === 'object' ? expected.user._id : expected.user) || expected.user;
        updatedData[userId] = {
          ...updatedData[userId],
          status: attendanceStatus,
          arrivedAt: attendanceStatus === 'present' ? new Date().toISOString() : updatedData[userId]?.arrivedAt
        };
      }
    });
    setAttendanceData(updatedData);
  };

  const saveAttendance = async () => {
    try {
      setUpdating(true);
      setError(null);

      // Build attendees array for API
      const attendeesArray = Object.entries(attendanceData)
        .filter(([_, data]) => data.status !== 'unmarked')
        .map(([userId, data]) => ({
          userId,
          status: data.status,
          arrivedAt: data.arrivedAt,
          notes: data.notes || ''
        }));

      const response = await liveEventApi.markAttendance(event._id, attendeesArray);

      // Calculate actual attendees (present + late)
      const counts = getAttendanceCounts();
      const actualAttendees = counts.present + counts.late;

      // Update liveSession with the response
      if (response.attendance) {
        setLiveSession(prev => ({
          ...prev,
          attendance: response.attendance
        }));
      }

      // Check if backend already updated metrics (Option 2 - automatic)
      if (!response.metrics || response.metrics.actualAttendees === undefined) {
        // Fallback: Update metrics separately (Option 1)
        await updateEventMetrics(actualAttendees);
      } else {
        console.log('✅ Metrics updated automatically by backend:', response.metrics);
      }

      setSuccessMessage(`Attendance saved! ${actualAttendees} attendee${actualAttendees !== 1 ? 's' : ''} confirmed.`);
      onEventUpdated?.();
    } catch (err) {
      console.error('Error saving attendance:', err);
      setError(err.message || 'Failed to save attendance');
    } finally {
      setUpdating(false);
    }
  };

  const updateEventMetrics = async (actualAttendees) => {
    try {
      // Update event metrics via API (fallback if backend doesn't do it automatically)
      await liveEventApi.updateEventMetrics(event._id, { actualAttendees });
      console.log('✅ Event metrics updated separately:', { actualAttendees });
    } catch (err) {
      console.warn('⚠️ Failed to update event metrics (may need backend endpoint):', err);
      // Don't throw - attendance was saved, this is just metrics
    }
  };

  const getAttendanceCounts = () => {
    const counts = {
      present: 0,
      absent: 0,
      late: 0,
      excused: 0,
      unmarked: 0,
      total: 0
    };

    Object.values(attendanceData).forEach(data => {
      counts[data.status]++;
      counts.total++;
    });

    return counts;
  };

  const getFilteredAttendees = () => {
    if (!liveSession?.attendance?.expectedAttendees) return [];

    let filtered = liveSession.attendance.expectedAttendees;

    // Apply search filter
    if (attendanceSearchTerm) {
      filtered = filtered.filter(expected => {
        // Handle both old format (user as object) and new denormalized format
        const user = typeof expected.user === 'object' ? expected.user : {};
        const name = expected.displayName || user.displayName || expected.email || user.email || '';
        return name.toLowerCase().includes(attendanceSearchTerm.toLowerCase());
      });
    }

    // Apply status filter
    if (attendanceFilter !== 'all') {
      filtered = filtered.filter(expected => {
        // Handle both old format (user as object) and new denormalized format
        const userId = (typeof expected.user === 'object' ? expected.user._id : expected.user) || expected.user;
        const status = attendanceData[userId]?.status || 'unmarked';
        return status === attendanceFilter;
      });
    }

    return filtered;
  };

  const groupAttendeesByRSVP = () => {
    const groups = {
      'Going': [],
      'TBD': [],
      'Not Going': []
    };

    getFilteredAttendees().forEach(expected => {
      const rsvpStatus = expected.rsvpStatus || 'TBD';
      if (groups[rsvpStatus]) {
        groups[rsvpStatus].push(expected);
      }
    });

    return groups;
  };

  const renderPeriodsTab = () => (
    <div className={styles.periodsTab}>
      {/* Period Scores */}
      {(() => {
        const shouldRender = schema?.periods?.enabled && liveSession.scorecard.score.periods;
        console.log('📊 Period section render check:', {
          periodsEnabled: schema?.periods?.enabled,
          hasPeriods: !!liveSession.scorecard.score.periods,
          periodCount: liveSession.scorecard.score.periods?.length,
          shouldRender
        });
        return shouldRender;
      })() && (
        <div className={styles.periodsSection}>
          <div className={styles.periodHeader}>
            <h3>Score by Period</h3>
            {(() => {
              const currentPeriod = liveSession.scorecard.score.periods.find(p => p.isActive);
              const currentOvertimePeriod = liveSession.scorecard.score.overtimePeriods?.find(p => p.isActive);
              const totalPeriods = schema.periods.count || 2;
              const completedCount = liveSession.scorecard.score.periods.filter(p => p.finishedAt).length;
              const allRegularPeriodsComplete = completedCount >= totalPeriods;
              const canStartNext = !currentPeriod && !currentOvertimePeriod && completedCount < totalPeriods;
              
              // Check overtime eligibility
              const overtimeEnabled = schema?.overtime?.enabled;
              const scoreIsTied = homeScore === awayScore;
              const overtimeCount = liveSession.scorecard.score.overtimePeriods?.length || 0;
              const maxOvertimeCount = schema?.overtime?.maxCount || 0;
              const canAddOvertime = allRegularPeriodsComplete && 
                                     !currentPeriod && 
                                     !currentOvertimePeriod &&
                                     overtimeEnabled && 
                                     scoreIsTied && 
                                     overtimeCount < maxOvertimeCount;
              
              // Debug overtime logic with detailed period info
              console.log('🏁 Overtime Debug:', {
                allRegularPeriodsComplete,
                currentPeriod: !!currentPeriod,
                currentOvertimePeriod: !!currentOvertimePeriod,
                overtimeEnabled,
                scoreIsTied,
                homeScore,
                awayScore,
                overtimeCount,
                maxOvertimeCount,
                canAddOvertime,
                completedCount,
                totalPeriods,
                periodsData: liveSession.scorecard.score.periods.map(p => ({
                  number: p.number,
                  name: p.name,
                  isActive: p.isActive,
                  finishedAt: p.finishedAt,
                  hasFinishedAt: !!p.finishedAt
                }))
              });
              
              return (
                <div className={styles.periodControls}>
                  {!isEventCompleted && (currentPeriod || currentOvertimePeriod) ? (
                    <button 
                      onClick={handleEndPeriod}
                      className={styles.endPeriodButton}
                      disabled={updating}
                      title={`End ${currentPeriod?.name || currentOvertimePeriod?.name}`}
                    >
                      <FaStopCircle /> End {currentPeriod?.name || currentOvertimePeriod?.name}
                    </button>
                  ) : !isEventCompleted && canStartNext ? (
                    <button 
                      onClick={handleStartPeriod}
                      className={styles.startPeriodButton}
                      disabled={updating}
                      title="Start the next period"
                    >
                      <FaPlayCircle /> Start Next Period
                    </button>
                  ) : !isEventCompleted && canAddOvertime ? (
                    <button 
                      onClick={handleAddOvertime}
                      className={styles.overtimeButton}
                      disabled={updating}
                      title="Add extra time / overtime period"
                    >
                      <FaPlus /> Add Extra Time
                    </button>
                  ) : null}
                  
                  {allRegularPeriodsComplete && !scoreIsTied && (
                    <div className={styles.noOvertimeNeeded}>
                      Winner decided - no extra time needed
                    </div>
                  )}
                  
                  {allRegularPeriodsComplete && scoreIsTied && !overtimeEnabled && (
                    <div className={styles.overtimeNotConfigured}>
                      ⚠️ Extra time not configured for this template. Game ended in a draw.
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
          <table className={styles.periodsTable}>
            <thead>
              <tr>
                <th>Period</th>
                <th>Home</th>
                <th>Away</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {/* Regular periods */}
              {liveSession.scorecard.score.periods.map((period, index) => (
                <tr key={`regular-${index}`} className={period.isActive ? styles.activePeriod : ''}>
                  <td>{period.name}</td>
                  <td>{period.homeScore}</td>
                  <td>{period.awayScore}</td>
                  <td>
                    {period.isActive ? (
                      <span className={styles.statusActive}>● Active</span>
                    ) : period.finishedAt ? (
                      <span className={styles.statusFinished}>✓ Finished</span>
                    ) : (
                      <span className={styles.statusPending}>○ Pending</span>
                    )}
                  </td>
                </tr>
              ))}
              
              {/* Overtime periods */}
              {liveSession.scorecard.score.overtimePeriods?.map((period, index) => (
                <tr 
                  key={`overtime-${index}`} 
                  className={`${period.isActive ? styles.activePeriod : ''} ${styles.overtimePeriod}`}
                >
                  <td>
                    <span className={styles.overtimeLabel}>⏱️</span>
                    {period.name}
                  </td>
                  <td>{period.homeScore}</td>
                  <td>{period.awayScore}</td>
                  <td>
                    {period.isActive ? (
                      <span className={styles.statusActive}>● Active</span>
                    ) : period.endedEarly ? (
                      <span className={styles.statusSuddenDeath}>⚡ Sudden Death</span>
                    ) : period.finishedAt ? (
                      <span className={styles.statusFinished}>✓ Finished</span>
                    ) : (
                      <span className={styles.statusPending}>○ Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderScoreTab = () => (
    <div className={styles.scoreTab}>
      <div className={styles.scoreDisplay}>
        <div className={styles.teamScore}>
          <div className={styles.teamLabel}>Home</div>
          <div className={styles.teamName}>{event.teams?.home?.name || 'Home Team'}</div>
          <div className={styles.scoreValue}>{homeScore}</div>
          <div className={styles.scoreControls}>
            <button 
              onClick={() => adjustScore('home', -1)} 
              className={styles.scoreButton}
              disabled={isEventCompleted || updating || homeScore === 0}
              title={isEventCompleted ? 'Event is completed' : ''}
            >
              <FaMinus />
            </button>
            <button 
              onClick={() => adjustScore('home', 1)} 
              className={styles.scoreButton}
              disabled={isEventCompleted || updating}
              title={isEventCompleted ? 'Event is completed' : ''}
            >
              <FaPlus />
            </button>
          </div>
        </div>

        <div className={styles.vs}>VS</div>

        <div className={styles.teamScore}>
          <div className={styles.teamLabel}>Away</div>
          <div className={styles.teamName}>{event.teams?.away?.name || 'Away Team'}</div>
          <div className={styles.scoreValue}>{awayScore}</div>
          <div className={styles.scoreControls}>
            <button 
              onClick={() => adjustScore('away', -1)} 
              className={styles.scoreButton}
              disabled={isEventCompleted || updating || awayScore === 0}
              title={isEventCompleted ? 'Event is completed' : ''}
            >
              <FaMinus />
            </button>
            <button 
              onClick={() => adjustScore('away', 1)} 
              className={styles.scoreButton}
              disabled={isEventCompleted || updating}
              title={isEventCompleted ? 'Event is completed' : ''}
            >
              <FaPlus />
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions for Point-Based Scoring */}
      {!isEventCompleted && schema?.scoring?.type === 'point-based' && schema?.scoring?.pointValues && schema.scoring.pointValues.length > 0 && (
        <div className={styles.quickActions}>
          <h3>Quick Score</h3>
          <div className={styles.quickActionGrid}>
            {schema.scoring.pointValues.map((pv, index) => (
              <div key={index} className={styles.pointValueSection}>
                <div className={styles.pointValueLabel}>
                  {pv.icon} {pv.label} ({pv.value}pts)
                </div>
                <div className={styles.pointValueButtons}>
                  <button 
                    onClick={() => adjustScore('home', pv.value)}
                    className={styles.quickScoreButton}
                    disabled={updating}
                  >
                    Home
                  </button>
                  <button 
                    onClick={() => adjustScore('away', pv.value)}
                    className={styles.quickScoreButton}
                    disabled={updating}
                  >
                    Away
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Render a single stat control with +/- buttons
  const renderStatControl = (team, stat, value) => {
    const increment = getStatIncrement(stat);
    const { min, max } = getStatBounds(stat);
    const isPercentage = stat.type === 'percentage';
    
    return (
      <div className={styles.statControlContainer}>
        {/* Minus Button */}
        <button
          className={`${styles.statBtn} ${isEventCompleted || value <= min ? styles.statBtnDisabled : ''}`}
          onClick={() => adjustStat(team, stat.key, -increment, stat)}
          disabled={isEventCompleted || value <= min}
          title={`Decrease by ${increment}`}
        >
          <FaMinus size={12} />
        </button>
        
        {/* Value Display/Input */}
        <input
          type="number"
          className={styles.statValueInput}
          value={value}
          onChange={(e) => {
            const newValue = parseFloat(e.target.value) || 0;
            setStatDirectly(team, stat.key, newValue, stat);
          }}
          min={min}
          max={max}
          disabled={isEventCompleted}
          title="Click to edit directly"
        />
        {isPercentage && <span className={styles.percentSymbol}>%</span>}
        
        {/* Plus Button */}
        <button
          className={`${styles.statBtn} ${styles.statBtnAdd} ${isEventCompleted || value >= max ? styles.statBtnAddDisabled : ''}`}
          onClick={() => adjustStat(team, stat.key, increment, stat)}
          disabled={isEventCompleted || value >= max}
          title={`Increase by ${increment}`}
        >
          <FaPlus size={12} />
        </button>
      </div>
    );
  };

  const renderStatisticsTab = () => (
    <div className={styles.statisticsTab}>
      {/* Auto-save indicator */}
      <div className={styles.autoSaveIndicator}>
        <FaCheckCircle size={14} />
        <span>Changes save automatically</span>
      </div>

      {/* Stats Table Layout */}
      <div className={styles.statsTable}>
        {/* Header Row */}
        <div className={styles.statsTableHeader}>
          <div className={styles.statsTableHeaderCell}>Home</div>
          <div className={styles.statsTableHeaderCellCenter}>Statistic</div>
          <div className={styles.statsTableHeaderCell}>Away</div>
        </div>

        {/* Stat Rows */}
        {schema?.statistics?.map((stat, index) => {
          const homeValue = homeStats[stat.key] ?? stat.defaultValue ?? 0;
          const awayValue = awayStats[stat.key] ?? stat.defaultValue ?? 0;
          const increment = getStatIncrement(stat);

          return (
            <div key={index} className={styles.statsTableRow}>
              {/* Home Team Controls */}
              <div className={styles.statsTableCell}>
                {renderStatControl('home', stat, homeValue)}
              </div>
              
              {/* Stat Label */}
              <div className={styles.statsTableCellCenter}>
                <span className={styles.statIcon}>{stat.icon || '📊'}</span>
                <span className={styles.statLabelText}>{stat.label}</span>
                {increment !== 1 && (
                  <span className={styles.statIncrementHint}>±{increment}</span>
                )}
              </div>
              
              {/* Away Team Controls */}
              <div className={styles.statsTableCell}>
                {renderStatControl('away', stat, awayValue)}
              </div>
            </div>
          );
        })}

        {/* Empty State */}
        {(!schema?.statistics || schema.statistics.length === 0) && (
          <div className={styles.emptyStats}>
            <FaChartBar size={32} />
            <p>No statistics configured</p>
            <span>Statistics tracking is not configured for this event's scorecard template</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderAttendanceTab = () => {
    if (!liveSession?.attendance?.enabled) {
      return (
        <div className={styles.attendanceDisabled}>
          <p>❌ Attendance tracking was not enabled for this event.</p>
        </div>
      );
    }

    const counts = getAttendanceCounts();
    const groupedAttendees = groupAttendeesByRSVP();

    return (
      <div className={styles.attendanceTab}>
        {/* Compact Header: Stats + Quick Actions in one row */}
        <div className={styles.attendanceHeader}>
          {/* Summary Stats - Inline */}
          <div className={styles.statCardsInline}>
            <div className={`${styles.statCardInline} ${styles.statPresent}`}>
              <span className={styles.statIconInline}>🟢</span>
              <span className={styles.statValueInline}>{counts.present}</span>
              <span className={styles.statLabelInline}>Present</span>
            </div>
            <div className={`${styles.statCardInline} ${styles.statLate}`}>
              <span className={styles.statIconInline}>🟡</span>
              <span className={styles.statValueInline}>{counts.late}</span>
              <span className={styles.statLabelInline}>Late</span>
            </div>
            <div className={`${styles.statCardInline} ${styles.statAbsent}`}>
              <span className={styles.statIconInline}>🔴</span>
              <span className={styles.statValueInline}>{counts.absent}</span>
              <span className={styles.statLabelInline}>Absent</span>
            </div>
            <div className={`${styles.statCardInline} ${styles.statExcused}`}>
              <span className={styles.statIconInline}>🔵</span>
              <span className={styles.statValueInline}>{counts.excused}</span>
              <span className={styles.statLabelInline}>Excused</span>
            </div>
            <div className={`${styles.statCardInline} ${styles.statUnmarked}`}>
              <span className={styles.statIconInline}>⚪</span>
              <span className={styles.statValueInline}>{counts.unmarked}</span>
              <span className={styles.statLabelInline}>Unmarked</span>
            </div>
          </div>

          {/* Quick Actions - Inline */}
          {!isEventCompleted && (
            <div className={styles.quickActionsInline}>
              <button 
                onClick={() => handleMarkAllAs('present')}
                className={`${styles.quickButtonInline} ${styles.quickButtonPresent}`}
                disabled={updating}
                title="Mark all as present"
              >
                All Present
              </button>
              <button 
                onClick={() => handleMarkRSVPGroup('Going', 'present')}
                className={`${styles.quickButtonInline} ${styles.quickButtonPresent}`}
                disabled={updating}
                title="Mark RSVP Going as present"
              >
                Going→Present
              </button>
              <button 
                onClick={() => handleMarkRSVPGroup('Not Going', 'absent')}
                className={`${styles.quickButtonInline} ${styles.quickButtonAbsent}`}
                disabled={updating}
                title="Mark RSVP Not Going as absent"
              >
                Not Going→Absent
              </button>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <div className={styles.attendanceControls}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder="🔍 Search attendees..."
              value={attendanceSearchTerm}
              onChange={(e) => setAttendanceSearchTerm(e.target.value)}
              className={styles.searchInput}
            />
          </div>
          <div className={styles.filterBox}>
            <label>Show:</label>
            <select
              value={attendanceFilter}
              onChange={(e) => setAttendanceFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">All ({counts.total})</option>
              <option value="unmarked">Unmarked ({counts.unmarked})</option>
              <option value="present">Present ({counts.present})</option>
              <option value="late">Late ({counts.late})</option>
              <option value="absent">Absent ({counts.absent})</option>
              <option value="excused">Excused ({counts.excused})</option>
            </select>
          </div>
        </div>

        {/* Attendee List Grouped by RSVP */}
        <div className={styles.attendeeList}>
          {Object.entries(groupedAttendees).map(([rsvpStatus, attendees]) => {
            if (attendees.length === 0) return null;

            return (
              <div key={rsvpStatus} className={styles.rsvpGroup}>
                <div className={styles.rsvpGroupHeader}>
                  <h4>
                    {rsvpStatus === 'Going' && '✅'} 
                    {rsvpStatus === 'TBD' && '❔'} 
                    {rsvpStatus === 'Not Going' && '❌'} 
                    RSVP: {rsvpStatus} ({attendees.length})
                  </h4>
                </div>
                <div className={styles.attendeeCards}>
                  {attendees.map((expected, index) => {
                    // Handle both old format (user as object) and new denormalized format (user as string with fields on expected)
                    const user = typeof expected.user === 'object' ? expected.user : { _id: expected.user };
                    const userId = user._id || expected.user;
                    const status = attendanceData[userId]?.status || 'unmarked';
                    
                    // Better display name handling - check denormalized fields first, then nested user object
                    const displayName = expected.displayName ||  // New denormalized format
                                       user.displayName || 
                                       user.name || 
                                       user.fullName || 
                                       user.firstName || 
                                       (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : null) ||
                                       expected.email?.split('@')[0] ||  // New denormalized format
                                       user.email?.split('@')[0] || // Use email username part as fallback
                                       'User';
                    const email = expected.email || user.email || '';  // Check denormalized first
                    const avatarLetter = (displayName || email || 'U')[0].toUpperCase();
                    
                    return (
                      <div key={userId} className={`${styles.attendeeCard} ${styles[`status_${status}`]}`}>
                        <div className={styles.attendeeInfo}>
                          <div className={styles.attendeeAvatar}>
                            {avatarLetter}
                          </div>
                          <div className={styles.attendeeDetails}>
                            <div className={styles.attendeeName}>
                              {displayName}
                            </div>
                            {email && displayName !== email && (
                              <div className={styles.attendeeEmail}>{email}</div>
                            )}
                          </div>
                        </div>
                        <div className={styles.attendeeStatus}>
                          <select
                            value={status}
                            onChange={(e) => handleAttendanceStatusChange(userId, e.target.value)}
                            className={`${styles.statusSelect} ${styles[`select_${status}`]}`}
                            disabled={isEventCompleted}
                          >
                            <option value="unmarked">Mark...</option>
                            <option value="present">🟢 Present</option>
                            <option value="late">🟡 Late</option>
                            <option value="absent">🔴 Absent</option>
                            <option value="excused">🔵 Excused</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Save Button */}
        {!isEventCompleted && (
          <div className={styles.attendanceSaveSection}>
            <button 
              onClick={saveAttendance}
              className={styles.saveAttendanceButton}
              disabled={updating}
            >
              {updating ? '💾 Saving...' : '💾 Save Attendance'}
            </button>
            <p className={styles.saveHint}>
              Changes are saved to the server when you click this button.
            </p>
          </div>
        )}
      </div>
    );
  };

  const renderEventLogTab = () => (
    <div className={styles.eventLogTab}>
      {!isEventCompleted && (
        <div className={styles.eventLogForm}>
          <h3>Quick Add Event</h3>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Type *</label>
              <select 
                value={eventLogType}
                onChange={(e) => setEventLogType(e.target.value)}
                className={styles.select}
              >
                <option value="">Select...</option>
                {schema?.eventTypes?.map((et, index) => (
                  <option key={index} value={et.type}>
                    {et.icon} {et.label}
                  </option>
                )) || null}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Team</label>
              <select 
                value={eventLogTeam}
                onChange={(e) => setEventLogTeam(e.target.value)}
                className={styles.select}
              >
                <option value="home">Home</option>
                <option value="away">Away</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Min</label>
              <input
                type="number"
                value={eventLogMinute}
                onChange={(e) => {
                  setEventLogMinute(e.target.value);
                  setMinuteAutoFilled(false);
                }}
                placeholder={currentTimer?.displayMinutes?.toString() || "0"}
                min="0"
                className={styles.input}
                style={{width: '60px'}}
              />
            </div>

            <div className={styles.formGroup} style={{flex: '1 1 auto'}}>
              <label>Description *</label>
              <input
                type="text"
                value={eventLogDescription}
                onChange={(e) => setEventLogDescription(e.target.value)}
                placeholder="e.g., Player #10 scored"
                className={styles.input}
              />
            </div>

            <button 
              onClick={addEventLog}
              className={styles.addButton}
              disabled={updating || !eventLogType || !eventLogDescription}
              style={{alignSelf: 'end'}}
            >
              {updating ? 'Adding...' : 'ADD'}
            </button>
          </div>
        </div>
      )}

      <div className={styles.eventLogList}>
        <h3><FaHistory /> Event History</h3>
        {!liveSession.scorecard.events || liveSession.scorecard.events.length === 0 ? (
          <p className={styles.noEvents}>No events logged yet</p>
        ) : (
          <div className={styles.events}>
            {[...liveSession.scorecard.events].reverse().map((evt, index) => {
              const eventType = schema?.eventTypes?.find(et => et.type === evt.type);
              return (
                <div key={index} className={styles.eventItem}>
                  <div className={styles.eventTime}>
                    {evt.minute ? `${evt.minute}'` : 'Start'}
                    {evt.period && <span className={styles.eventPeriod}> - {evt.period}</span>}
                  </div>
                  <div className={styles.eventContent}>
                    <div className={styles.eventHeader}>
                      <div className={styles.eventTeam}>
                        {evt.team === 'home' ? event.teams?.home?.name || 'Home' : event.teams?.away?.name || 'Away'}
                      </div>
                      {eventType && (
                        <div className={styles.eventType}>
                          {eventType.icon} {eventType.label}
                        </div>
                      )}
                    </div>
                    <div className={styles.eventDescription}>{evt.description}</div>
                    {evt.points > 0 && (
                      <div className={styles.eventPoints}>+{evt.points}pts</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        {/* Compact Header with Inline Timer */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2>{event.title}</h2>
            <div className={styles.headerInfo}>
              <p className={isEventCompleted ? styles.completedIndicator : styles.liveIndicator}>
                {isEventCompleted ? (
                  <>
                    <FaCheckCircle /> EVENT COMPLETED
                  </>
                ) : (
                  <>
                    <span className={styles.liveDot}></span>
                    LIVE
                  </>
                )}
              </p>
              
              {/* Inline Compact Timer */}
              <div className={styles.headerTimerInline}>
                <GameTimer 
                  eventId={event._id} 
                  isEventCompleted={isEventCompleted}
                  onTimerUpdate={handleTimerUpdate}
                  compact={true}
                  refreshKey={timerRefreshKey}
                  hasActivePeriod={gameState.hasActivePeriod}
                  allPeriodsComplete={gameState.allPeriodsComplete}
                  gameDecided={gameState.gameDecided}
                />
              </div>
            </div>
          </div>

          <div className={styles.headerActions}>
            {!isEventCompleted && (
              <button 
                onClick={finishEvent}
                className={styles.finishButton}
                disabled={updating}
              >
                <FaStop /> Finish Event
              </button>
            )}
            <button onClick={onClose} className={styles.closeButton}>
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className={styles.errorBanner}>
            ❌ {error}
          </div>
        )}

        {successMessage && (
          <div className={styles.successBanner}>
            <FaCheckCircle /> {successMessage}
          </div>
        )}

        {/* Horizontal Tabs */}
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${activeTab === 'score' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('score')}
          >
            Score
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'statistics' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('statistics')}
          >
            Statistics
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'log' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('log')}
          >
            Event Log ({liveSession.scorecard.events?.length || 0})
          </button>
          {liveSession?.attendance?.enabled && (
            <button 
              className={`${styles.tab} ${activeTab === 'attendance' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('attendance')}
            >
              👥 Attendance ({(() => {
                const marked = Object.values(attendanceData).filter(d => d.status !== 'unmarked').length;
                const total = Object.keys(attendanceData).length;
                return `${marked}/${total}`;
              })()})
            </button>
          )}
          {schema?.periods?.enabled && (
            <button 
              className={`${styles.tab} ${activeTab === 'periods' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('periods')}
            >
              Periods
            </button>
          )}
        </div>

        {/* Content Area */}
        <div className={styles.content}>
          {activeTab === 'score' && renderScoreTab()}
          {activeTab === 'statistics' && renderStatisticsTab()}
          {activeTab === 'attendance' && renderAttendanceTab()}
          {activeTab === 'log' && renderEventLogTab()}
          {activeTab === 'periods' && renderPeriodsTab()}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <p className={styles.lastUpdated}>
            Last updated: {new Date(liveSession.scorecard.lastUpdated).toLocaleTimeString()}
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LiveScorecardModal;

